import { color } from '@/shared/styles/assets/colors';
import { createTheme, responsiveFontSizes } from '@mui/material';
import type { PaletteColor, PaletteColorOptions, Shadows } from '@mui/material/styles';
import type {} from '@mui/x-data-grid-pro/themeAugmentation';
import type {} from '@mui/x-data-grid/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { componentStyleOverrides } from './mui/component-overrides';
import { themePalette } from './mui/palette';
import { shadows } from './mui/shadows';
import { themeTypography } from './mui/typography';

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true;
    xxxl: true;
  }
  interface Palette {
    accent: PaletteColor;
  }
  interface PaletteOptions {
    accent?: PaletteColorOptions;
  }
  interface Theme {
    customization: {
      fontFamily: string;
      borderRadius: number;
    };
  }
  interface ThemeOptions {
    customization?: {
      fontFamily?: string;
      borderRadius?: number;
    };
  }
}

const themeOption = {
  colors: color,
  heading: color.grey900,
  paper: color.paper,
  backgroundDefault: color.white,
  background: color.white,
  darkTextPrimary: color.grey700,
  darkTextSecondary: color.grey900,
  textDark: color.grey900,
  menuSelected: color.truentityBlue[700],
  menuSelectedBack: color.truentityBlue[50],
  divider: color.grey400,
  customization: {
    fontFamily: `'Roboto', Helvetica, Arial, sans-serif`,
    borderRadius: 8
  }
};

let theme = createTheme({
  direction: 'ltr',
  palette: { ...themePalette(themeOption), DataGrid: { bg: color.paper, pinnedBg: color.white } },
  // Align the standard MUI shape token with the project radius (was only in `customization`),
  // so components can rely on theme.shape.borderRadius instead of hardcoding 8.
  shape: { borderRadius: themeOption.customization.borderRadius },
  // Wire the project's custom elevation scale (previously defined in mui/shadows.ts but never
  // wired into the theme, so theme.shadows fell back to MUI defaults). This lets components use
  // theme.shadows[n] instead of hardcoded boxShadow strings.
  shadows: shadows as unknown as Shadows,
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 768,
      lg: 1024,
      xl: 1280,
      xxl: 1536,
      xxxl: 1728
    }
  },
  typography: themeTypography(themeOption),
  components: componentStyleOverrides(themeOption),
  customization: themeOption.customization
});
theme = responsiveFontSizes(theme);
export { theme };
